import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getBlob,
} from 'firebase/storage';
import { storage } from '../config/firebase';
import {
  CloudStorageProvider,
  UploadProgress,
  UploadResult,
} from './CloudStorageAdapter';

export class FirebaseStorageProvider extends CloudStorageProvider {
  async uploadFile(
    file: File,
    path: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const progress: UploadProgress = {
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              percentage:
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            };
            onProgress(progress);
          }
        },
        (error) => {
          reject(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url,
              path,
              size: uploadTask.snapshot.totalBytes,
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  async downloadFile(path: string): Promise<Blob> {
    const storageRef = ref(storage, path);
    return await getBlob(storageRef);
  }

  async deleteFile(path: string): Promise<void> {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  }

  async getFileUrl(path: string): Promise<string> {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  }

  async listFiles(path: string): Promise<string[]> {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);
    return result.items.map((item) => item.fullPath);
  }
}

// Singleton instance
export const firebaseStorageProvider = new FirebaseStorageProvider();
